<template>
    <OnboardingLayout v-bind="appSharedData.getOnboardingLayoutProps()">
        <template #content>
            <OnboardingStepContainer
                :showStepsIndicator="true"
                :stepNumber="4"
            >
                <template #title> When are you usually available? </template>
                <template #subtitle>
                    Pick a simple preset now. You can fine-tune your hours
                    later.
                </template>
                <template #content>
                    <template
                        v-if="
                            dummyPreDefinedAvailabilityType !==
                            PreDefinedAvailabilityTypeEnum.CustomHours
                        "
                    >
                        <div class="block w-full">
                            <ApiForm
                                method="POST"
                                :url="
                                    urlFactory.makeOnboardingUpdatePredefinedAvailabilityUrl()
                                "
                                ref="apiFormRef"
                                class="w-full"
                            >
                                <input
                                    type="hidden"
                                    :name="
                                        RequestBodyKeyEnum.App_Onboarding_PreDefinedAvailabilityType
                                    "
                                    :value="dummyPreDefinedAvailabilityType"
                                />

                                <div class="space-y-8">
                                    <FormControl
                                        hideLabel
                                        :error="
                                            apiFormRef?.response?.getValidationError(
                                                RequestBodyKeyEnum.App_Onboarding_PreDefinedAvailabilityType,
                                            )
                                        "
                                    >
                                        <div class="grid grid-cols-12 gap-4">
                                            <div
                                                class="col-span-12 md:col-span-6"
                                            >
                                                <OnboardingStepAvailabilityItemCard
                                                    v-model="
                                                        dummyPreDefinedAvailabilityType
                                                    "
                                                    :preDefinedAvailabilityType="
                                                        PreDefinedAvailabilityTypeEnum.Weekdays9to5
                                                    "
                                                    title="Weekdays 9 to 5"
                                                    description="Classic business hours."
                                                />
                                            </div>
                                            <div
                                                class="col-span-12 md:col-span-6"
                                            >
                                                <OnboardingStepAvailabilityItemCard
                                                    v-model="
                                                        dummyPreDefinedAvailabilityType
                                                    "
                                                    :preDefinedAvailabilityType="
                                                        PreDefinedAvailabilityTypeEnum.WeekdayMornings
                                                    "
                                                    title="Weekday mornings"
                                                    description="Best for focused sessions before lunch."
                                                />
                                            </div>
                                            <div
                                                class="col-span-12 md:col-span-6"
                                            >
                                                <OnboardingStepAvailabilityItemCard
                                                    v-model="
                                                        dummyPreDefinedAvailabilityType
                                                    "
                                                    :preDefinedAvailabilityType="
                                                        PreDefinedAvailabilityTypeEnum.WeekdayAfternoons
                                                    "
                                                    title="Weekday afternoons"
                                                    description="Simple afternoon-only availability."
                                                />
                                            </div>
                                            <div
                                                class="col-span-12 md:col-span-6"
                                            >
                                                <OnboardingStepAvailabilityItemCard
                                                    v-model="
                                                        dummyPreDefinedAvailabilityType
                                                    "
                                                    :preDefinedAvailabilityType="
                                                        PreDefinedAvailabilityTypeEnum.CustomHours
                                                    "
                                                    title="Custom hours"
                                                    description="Open the detailed editor."
                                                />
                                            </div>
                                        </div>
                                    </FormControl>
                                    <div>
                                        <div class="text-sm text-gray-600">
                                            Don't worry, you can customize your
                                            availability later.
                                        </div>
                                    </div>
                                </div>
                            </ApiForm>
                        </div>
                    </template>
                    <template v-else>
                        <div class="block w-full">
                            <ApiForm
                                method="POST"
                                :url="
                                    urlFactory.makeOnboardingUpdateCustomAvailabilityUrl()
                                "
                                ref="apiFormRef"
                            >
                                <div class="max-w-xl mx-auto w-full">
                                    <div
                                        @click="
                                            handleBackToPreDefinedAvailabilityTypes()
                                        "
                                        class="inline-flex items-center gap-2 py-2 cursor-pointer group"
                                    >
                                        <Icon
                                            name="angle-left"
                                            class="w-4 h-4 text-gray-500"
                                        />
                                        <span class="group-hover:underline">
                                            Back
                                        </span>
                                    </div>
                                    <div class="space-y-4">
                                        <Card>
                                            <template #content>
                                                <AvailabilityWeeklyHoursForm
                                                    :availability="
                                                        localCompanyUserAggregate.availability
                                                    "
                                                    :errors="
                                                        apiFormRef?.response?.getErrors() ??
                                                        new ApiFormErrors({})
                                                    "
                                                />
                                            </template>
                                        </Card>
                                    </div>
                                </div>
                            </ApiForm>
                        </div>
                    </template>
                </template>
                <template #action></template>
            </OnboardingStepContainer>
        </template>

        <template #footerRight>
            <Button
                @click="apiFormRef?.submit()"
                color="primary"
                :iconRight="true"
                :loading="apiFormRef?.loading.valueOf()"
            >
                Continue
            </Button>
        </template>
    </OnboardingLayout>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import { ref } from 'vue'
    import OnboardingLayout from '@/Shared/Layout/Onboarding/OnboardingLayout.vue'
    import Button from '@/Shared/Component/Button.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import OnboardingStepContainer from '@/App/Company/Component/OnboardingStepContainer.vue'
    import Card from '@/Shared/Component/Card.vue'
    import AvailabilityWeeklyHoursForm from '@/App/Availability/Component/AvailabilityWeeklyHoursForm.vue'
    import ApiFormErrors from '@/Shared/Component/Forms/ApiFormErrors'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import Availability from '@/App/Availability/Entity/Availability'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import OnboardingStepAvailabilityItemCard from '@/App/Company/Component/OnboardingStepAvailabilityItemCard.vue'
    import type { CompanyUserAggregateData } from '@/Shared/AutoGenerated/Main'
    import { PreDefinedAvailabilityTypeEnum } from '@/Shared/AutoGenerated/Main'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import Icon from '@/Shared/Component/Icon.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'

    interface ShowOnboardingStepAvailabilityProps {
        appSharedDataData: any
        companyUserAggregateData: CompanyUserAggregateData
    }

    const props = defineProps<ShowOnboardingStepAvailabilityProps>()
    const urlFactory = new UrlFactory()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const localCompanyUserAggregate = CompanyUserAggregate.hydrateSingle(
        props.companyUserAggregateData,
    )
    const apiFormRef = ref<ApiFormRefType>(null)

    const getAvailability = (): Availability => {
        if (!localCompanyUserAggregate.availability) {
            throw new Error('Availability should not by missing')
        }

        return localCompanyUserAggregate.availability
    }

    const dummyPreDefinedAvailabilityType =
        ref<PreDefinedAvailabilityTypeEnum>()

    function handleBackToPreDefinedAvailabilityTypes(): void {
        dummyPreDefinedAvailabilityType.value = undefined
    }
</script>
